import React from "react";
import { useHistory } from "react-router-dom";
import { ArrowRight, Clock, MapPin, Star } from "lucide-react";
import SpaceSearchBar, { SpaceSearchPayload } from "../../../components/SpaceSearchBar";

type Notification = {
  emoji: string;
  title: string;
  subtitle: string;
  status: string;
  statusColor: string;
  // Position relative to the phone from the Figma frame (sm and up)
  className: string;
  // Phones only show two cards, tucked against the edges so the app screen stays visible
  mobileClassName?: string;
};

const NOTIFICATIONS: Notification[] = [
  {
    emoji: "🏢",
    title: "Meeting Room Confirmed",
    subtitle: "Nairobi Garage • 10AM",
    status: "Confirmed",
    statusColor: "#4ade80",
    className: "sm:left-[52px] sm:top-[22px]",
    mobileClassName: "-right-[40px] top-[64px]",
  },
  {
    emoji: "✅",
    title: "Membership Active",
    subtitle: "Business Plan",
    status: "Active",
    statusColor: "#4ade80",
    className: "sm:-left-[9px] sm:top-[87px]",
  },
  {
    emoji: "💻",
    title: "Hot Desk Available",
    subtitle: "iHub Westlands",
    status: "Open Now",
    statusColor: "#22d3ee",
    className: "sm:left-[102px] sm:top-[153px]",
  },
  {
    emoji: "🏨",
    title: "Hotel Reserved",
    subtitle: "Accra Central • 2 nights",
    status: "Reserved",
    statusColor: "#a78bfa",
    className: "sm:left-[86px] sm:top-[257px]",
  },
  {
    emoji: "🎉",
    title: "Event Registration Complete",
    subtitle: "Networking Night • 6PM",
    status: "Registered",
    statusColor: "#fb923c",
    className: "sm:left-[17px] sm:top-[366px]",
    mobileClassName: "-left-[40px] top-[392px]",
  },
];

function PhoneMockup() {
  return (
    <div className="relative h-[490px] w-[240px]">
      <div
        className="absolute inset-0 overflow-hidden rounded-[38px] border-[1px] border-solid border-white/15 shadow-[0_0_80px_0_rgba(74,222,128,0.1),inset_0_1px_0_0_rgba(255,255,255,0.08)]"
        style={{ backgroundImage: "linear-gradient(180deg, #0f172a 0%, #020c1b 100%)" }}
      >
        <div className="flex items-center justify-between px-[20px] pb-2 pt-[16px] text-[10px] leading-[15px] text-white/50">
          <span>9:41</span>
          <span className="h-4 w-16 rounded-full bg-black" />
          <span>●●●</span>
        </div>
        <div className="px-[16px] pt-1">
          <div className="flex items-center gap-2">
            <span className="flex size-6 items-center justify-center rounded-[10px] bg-[#4ade80] text-[12px] font-black text-[#020817]">
              S
            </span>
            <span className="text-[12px] font-bold text-white">SPACIA</span>
          </div>
          <p className="pt-[12px] text-[10px] leading-[15px] text-[#90a1b9]">Good morning, Amara 👋</p>
          <div className="mt-2 flex items-center gap-1.5 rounded-[14px] border-[1px] border-solid border-white/[0.08] bg-white/5 px-[11px] py-[7px]">
            <MapPin className="size-2.5 text-[#4ade80]" />
            <span className="text-[10px] leading-[15px] text-[#90a1b9]">Search spaces, hotels, events...</span>
          </div>
          <div className="flex gap-1.5 pt-[12px] text-[10px] font-semibold leading-[15px]">
            <span className="rounded-full bg-[#4ade80] px-2.5 py-1 text-[#020817]">Work</span>
            <span className="rounded-full bg-white/5 px-2.5 py-1 text-[#90a1b9]">Stay</span>
            <span className="rounded-full bg-white/5 px-2.5 py-1 text-[#90a1b9]">Play</span>
          </div>
          <div className="mt-[12px] overflow-hidden rounded-2xl border-[1px] border-solid border-white/[0.08] bg-white/5">
            <div
              className="flex h-20 items-center justify-center text-[30px]"
              style={{ backgroundImage: "linear-gradient(158deg, rgba(49, 44, 133, 0.6) 0%, #0f172a 100%)" }}
            >
              🏢
            </div>
            <div className="p-2.5 text-[10px] leading-[15px]">
              <p className="font-bold text-white">Nairobi Garage – Westlands</p>
              <p className="text-[#90a1b9]">Hot Desk • KES 1,200/day</p>
              <div className="flex items-center gap-1 pt-1">
                <Star className="size-2 fill-[#4ade80] text-[#4ade80]" />
                <span className="text-[#4ade80]">4.9</span>
                <span className="text-[#62748e]">• 128 reviews</span>
              </div>
            </div>
          </div>
          <div className="mt-2.5 flex items-center justify-between rounded-[14px] border-[1px] border-solid border-[#4ade80]/20 bg-[#4ade80]/10 px-[11px] py-[9px]">
            <div className="text-[10px] leading-[15px]">
              <p className="font-semibold text-white">Book Instantly</p>
              <p className="flex items-center gap-0.5 text-[#90a1b9]">
                <Clock className="size-2" /> Instant confirmation
              </p>
            </div>
            <span className="flex size-6 items-center justify-center rounded-full bg-[#4ade80]">
              <ArrowRight className="size-2.5 text-[#020817]" />
            </span>
          </div>
        </div>
      </div>

      {NOTIFICATIONS.map(n => (
        <div
          key={n.title}
          className={`absolute min-w-[148px] rounded-2xl border-[1px] border-solid border-white/10 bg-[rgba(13,27,53,0.95)] p-[13px] shadow-[0_8px_32px_0_rgba(0,0,0,0.5)] sm:right-auto ${
            n.mobileClassName ?? "hidden sm:block"
          } ${n.className}`}
        >
          <div className="flex items-center gap-2 pb-1.5">
            <span className="text-[16px] leading-6">{n.emoji}</span>
            <div>
              <p className="whitespace-nowrap text-[11px] font-semibold leading-[13.75px] text-white">{n.title}</p>
              <p className="whitespace-nowrap text-[10px] leading-[15px] text-[#90a1b9]">{n.subtitle}</p>
            </div>
          </div>
          <span
            className="inline-block rounded-full px-2 text-[10px] font-semibold leading-4"
            style={{ color: n.statusColor, backgroundColor: `${n.statusColor}21` }}
          >
            {n.status}
          </span>
        </div>
      ))}
    </div>
  );
}

type HeroSectionProps = {
  onSearch: (payload: SpaceSearchPayload) => void;
};

export default function HeroSection({ onSearch }: HeroSectionProps) {
  const history = useHistory();

  return (
    <section className="relative overflow-hidden bg-white">
      {/* Background glows */}
      <div className="pointer-events-none absolute left-0 top-[342px] h-[500px] w-[628px] rounded-full bg-[#017bef]/[0.06] blur-[64px]" />
      <div className="pointer-events-none absolute left-[72%] top-[626px] size-[400px] rounded-full bg-[#0062c0]/5 blur-[64px]" />

      <div className="relative mx-auto max-w-[1440px] px-[16px] pb-20 pt-28 sm:px-8 lg:px-12 lg:pt-[108px]">
        <div className="grid grid-cols-1 items-center gap-16 lg:min-h-[604px] lg:grid-cols-2">
          <div>
            <h1 className="text-[40px] font-bold leading-[1.08] tracking-[-1.6px] text-[#0a0a0a] sm:text-[64px] sm:leading-[69px]">
              One Platform for
              <span className="block pt-2 text-[#017bef]">
                Work • <span className="text-[#60a5fa]">Stay</span> • <span className="text-[#0ea5e9]">Play</span>
              </span>
            </h1>
            <div className="max-w-[480px] space-y-[26px] pt-10 text-[16px] leading-[26px] text-[#717182] sm:pt-[82px]">
              <p>
                Modern life doesn't move in straight lines. Your day might call for a quiet morning desk, an overnight
                stay in a new city, and an evening at a great restaurant or spa. Managing three different apps,
                accounts, and payment systems to make that happen is a waste of time.
              </p>
              <p>
                SPACIA eliminates that friction. Discover, book, and manage every space you need from a single, unified
                app
              </p>
            </div>
            <div className="flex flex-wrap gap-[16px] pt-8">
              <button
                type="button"
                onClick={() => history.push("/workspaces-search")}
                className="inline-flex items-center gap-2 rounded-full bg-[#017bef] px-7 py-3.5 text-[14px] font-bold leading-5 text-white drop-shadow-[0_4px_8px_rgba(1,123,239,0.25)] transition-colors hover:bg-[#0062c0]"
              >
                Explore Spaces
                <ArrowRight className="size-4" />
              </button>
              <button
                type="button"
                onClick={() => history.push("/package-signup")}
                className="rounded-full border-[1px] border-solid border-[#e5e7eb] px-7 py-3.5 text-[14px] font-semibold leading-5 text-[#0a0a0a] transition-colors hover:bg-gray-50"
              >
                Get Started
              </button>
            </div>
          </div>

          <div className="relative flex justify-center">
            <div className="pointer-events-none absolute left-1/2 top-1/2 h-[735px] w-[899px] max-w-[140%] -translate-x-1/2 -translate-y-1/2 rounded-full bg-[#4ade80]/[0.08] blur-[96px]" />
            <div className="pointer-events-none absolute left-1/2 top-1/2 h-[531px] w-[527px] max-w-full -translate-x-1/2 -translate-y-1/2 rounded-full bg-[#017bef]/50 blur-[64px]" />
            <div className="relative scale-[0.85] sm:scale-100">
              <PhoneMockup />
            </div>
          </div>
        </div>

        <div className="pt-12">
          <SpaceSearchBar onSearch={onSearch} />
        </div>
      </div>
    </section>
  );
}
